import { useContext, useState, useRef, useEffect } from 'react';
import { __ } from '@wordpress/i18n';
import SbUtils from '../../../Utils/SbUtils';
import ReviewAlertsScreenContext from '../../Context/ReviewAlertsScreenContext';
import Select from '../../Common/Select';
import Button from '../../Common/Button';
import Checkbox from '../../Common/Checkbox';
import Pagination from '../../Common/Pagination';
import Switcher from '../../Common/Switcher';

/**
 * Type icons for different visibility item types
 */
const TypeIcons = {
    page: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path fillRule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clipRule="evenodd" />
            <path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z" />
        </svg>
    ),
    category: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M19.5 21a3 3 0 0 0 3-3v-4.5a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h15ZM1.5 10.146V6a3 3 0 0 1 3-3h5.379a2.25 2.25 0 0 1 1.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 0 1 3 3v1.146A4.483 4.483 0 0 0 19.5 9h-15a4.483 4.483 0 0 0-3 1.146Z" />
        </svg>
    ),
    post_type: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M5.566 4.657A4.505 4.505 0 0 1 6.75 4.5h10.5c.41 0 .806.055 1.183.157A3 3 0 0 0 15.75 3h-7.5a3 3 0 0 0-2.684 1.657ZM2.25 12a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3v-6ZM5.25 7.5c-.41 0-.806.055-1.184.157A3 3 0 0 1 6.75 6h10.5a3 3 0 0 1 2.683 1.657A4.505 4.505 0 0 0 18.75 7.5H5.25Z" />
        </svg>
    )
};

/**
 * DisplayedOnTooltip - Shows a tooltip with location details on hover
 */
const DisplayedOnTooltip = ( { popup, availablePages, availableCategories, availablePostTypes } ) => {
    const [ isOpen, setIsOpen ] = useState( false );
    const timeoutRef = useRef( null );

    const visibility = popup.settings?.visibility;
    const displayOn = visibility?.display_on;
    const isActive = popup.status === 'active';

    // Handle mouse enter with small delay
    const handleMouseEnter = () => {
        if ( timeoutRef.current ) {
            clearTimeout( timeoutRef.current );
        }
        timeoutRef.current = setTimeout( () => {
            setIsOpen( true );
        }, 150 );
    };

    // Handle mouse leave with small delay to allow moving to tooltip
    const handleMouseLeave = () => {
        if ( timeoutRef.current ) {
            clearTimeout( timeoutRef.current );
        }
        timeoutRef.current = setTimeout( () => {
            setIsOpen( false );
        }, 100 );
    };

    // Cleanup timeout on unmount
    useEffect( () => {
        return () => {
            if ( timeoutRef.current ) {
                clearTimeout( timeoutRef.current );
            }
        };
    }, [] );

    // If draft, just show "Draft" without tooltip
    if ( !isActive ) {
        return <span className="sb-displayed-on-draft">{ __( 'Draft', 'reviews-feed' ) }</span>;
    }

    // If showing on all pages
    if ( displayOn === 'all' || displayOn === undefined ) {
        const excludeCount = countItems( visibility?.excluded );
        const text = excludeCount > 0
            ? __( 'All pages', 'reviews-feed' ) + ` (${excludeCount} ` + __( 'excluded', 'reviews-feed' ) + ')'
            : __( 'All pages', 'reviews-feed' );
        return <span className="sb-displayed-on-all sb-bold">{ text }</span>;
    }

    // Collect all items for specific mode (with hydration from available items)
    const items = collectVisibilityItems( visibility?.specific, availablePages, availableCategories, availablePostTypes );
    const totalCount = items.length;

    // If no items configured
    if ( totalCount === 0 ) {
        return <span className="sb-displayed-on-empty">{ __( 'Not configured', 'reviews-feed' ) }</span>;
    }

    const summaryText = totalCount + ' ' + ( totalCount === 1 ? __( 'location', 'reviews-feed' ) : __( 'locations', 'reviews-feed' ) );

    return (
        <div
            className="sb-displayed-on-tooltip-wrapper"
            onMouseEnter={ handleMouseEnter }
            onMouseLeave={ handleMouseLeave }
        >
            <span className="sb-displayed-on-trigger">
                { summaryText }
            </span>
            { isOpen && (
                <div className="sb-displayed-on-tooltip">
                    <div className="sb-displayed-on-tooltip-arrow" />
                    <div className="sb-displayed-on-tooltip-header">
                        <span>{ __( 'This review alert is displayed on', 'reviews-feed' ) } </span>
                        <strong>{ totalCount } { totalCount === 1 ? __( 'page', 'reviews-feed' ) : __( 'pages', 'reviews-feed' ) }</strong>
                    </div>
                    <div className="sb-displayed-on-tooltip-list">
                        { items.map( ( item, index ) => (
                            <div key={ index } className="sb-displayed-on-tooltip-item">
                                <div className="sb-displayed-on-item-icon">
                                    { TypeIcons[ item.type ] || TypeIcons.page }
                                </div>
                                <div className="sb-displayed-on-item-content">
                                    <div className="sb-displayed-on-item-name">{ item.name }</div>
                                    { item.meta && (
                                        <div className="sb-displayed-on-item-meta">{ item.meta }</div>
                                    ) }
                                </div>
                                <div className="sb-displayed-on-item-badge">
                                    { item.typeLabel }
                                </div>
                            </div>
                        ) ) }
                    </div>
                </div>
            ) }
        </div>
    );
};

/**
 * Count items in a visibility group (helper for DisplayedOnTooltip)
 */
const countItems = ( group ) => {
    if ( !group ) return 0;
    const pages = group.pages?.length || 0;
    const categories = group.categories?.length || 0;
    const postTypes = group.custom_post_types?.length || 0;
    return pages + categories + postTypes;
};

/**
 * Collect visibility items into a flat array with type info
 * Hydrates ID-only data with metadata from available items
 * - Pages: { id, title, url } or just ID
 * - Categories: { id, name, url } or just ID
 * - Custom Post Types: { name (slug), label } or just slug
 */
const collectVisibilityItems = ( specific, availablePages = [], availableCategories = [], availablePostTypes = [] ) => {
    if ( !specific ) return [];

    const items = [];

    // Helper to find page by ID
    const findPage = ( id ) => availablePages.find( p => p.id === id );
    const findCategory = ( id ) => availableCategories.find( c => c.id === id );
    const findPostType = ( slug ) => availablePostTypes.find( pt => pt.name === slug );

    // Pages - hydrate ID-only format with available data
    ( specific.pages || [] ).forEach( page => {
        const isObject = typeof page === 'object' && page !== null;
        let name, meta;

        if ( isObject && page.title ) {
            // New format with full data
            name = page.title;
            meta = page.url || '';
        } else {
            // Old format (ID-only) or object missing title - hydrate from available
            const pageId = isObject ? page.id : page;
            const found = findPage( pageId );
            name = found?.title || __( 'Page', 'reviews-feed' ) + ' #' + pageId;
            meta = found?.url || '';
        }

        items.push( {
            type: 'page',
            typeLabel: __( 'Page', 'reviews-feed' ),
            name,
            meta
        } );
    } );

    // Categories - hydrate ID-only format with available data
    ( specific.categories || [] ).forEach( cat => {
        const isObject = typeof cat === 'object' && cat !== null;
        let name, meta;

        if ( isObject && cat.name ) {
            // New format with full data
            name = cat.name;
            meta = cat.url || '';
        } else {
            // Old format (ID-only) - hydrate from available
            const catId = isObject ? cat.id : cat;
            const found = findCategory( catId );
            name = found?.name || __( 'Category', 'reviews-feed' ) + ' #' + catId;
            meta = found?.url || '';
        }

        items.push( {
            type: 'category',
            typeLabel: __( 'Category', 'reviews-feed' ),
            name,
            meta
        } );
    } );

    // Custom Post Types - hydrate slug-only format with available data
    ( specific.custom_post_types || [] ).forEach( pt => {
        const isObject = typeof pt === 'object' && pt !== null;
        let name, slug;

        if ( isObject && pt.label ) {
            // New format with full data
            name = pt.label;
            slug = pt.name || '';
        } else {
            // Old format (slug-only) - hydrate from available
            slug = isObject ? pt.name : pt;
            const found = findPostType( slug );
            name = found?.label || slug;
        }

        items.push( {
            type: 'post_type',
            typeLabel: __( 'Post Type', 'reviews-feed' ),
            name,
            meta: slug
        } );
    } );

    return items;
};

const ReviewAlertsList = () => {
    const {
        sbCustomizer,
        popupsList,
        setPopupsList,
        popupsCount,
        setPopupsCount,
        editorTopLoader,
        editorNotification,
        editorConfirmDialog,
        isPro,
        upsellModal
    } = useContext( ReviewAlertsScreenContext );

    // Get available pages/categories/post types for tooltip hydration
    const availablePages = sbCustomizer?.wordpressPageLists || [];
    const availableCategories = sbCustomizer?.wordpressCategories || [];
    const availablePostTypes = sbCustomizer?.wordpressPostTypes || [];

    const [ selectedPopups, setSelectedPopups ] = useState( [] );
    const [ bulkAction, setBulkAction ] = useState( '' );
    const [ currentPage, setCurrentPage ] = useState( 1 );
    const [ isLoading, setIsLoading ] = useState( false );
    const perPage = 20;
    // Calculate initial totalPages from popupsCount
    const initialTotalPages = Math.ceil( ( popupsCount || 0 ) / perPage ) || 1;
    const [ totalPages, setTotalPages ] = useState( initialTotalPages );

    const bulkActionsList = {
        '': __( 'Bulk Actions', 'reviews-feed' ),
        'delete': __( 'Delete', 'reviews-feed' )
    };

    /**
     * Apply bulk action
     */
    const applyBulkAction = () => {
        if ( bulkAction === 'delete' && selectedPopups.length > 0 ) {
            deletePopup( null, false );
        }
    };

    /**
     * Handle duplicate popup
     */
    const duplicatePopup = ( popupId ) => {
        if ( !isPro ) {
            SbUtils.openUpsellModal( 'reviewAlertMultiple', upsellModal );
            return;
        }

        const formData = {
            action: 'sbr_review_alert_duplicate',
            nonce: sbCustomizer.nonce,
            id: popupId
        };

        const notificationsContent = {
            success: {
                icon: 'success',
                text: __( 'Review alert duplicated', 'reviews-feed' )
            }
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => {
                if ( data?.data?.popupsList ) {
                    setPopupsList( data.data.popupsList );
                }
                if ( data?.data?.popupsCount !== undefined ) {
                    setPopupsCount( data.data.popupsCount );
                }
                setSelectedPopups( [] );
            },
            editorTopLoader,
            editorNotification,
            notificationsContent
        );
    };

    /**
     * Confirm and delete popup(s)
     */
    const deletePopupConfirm = ( popupId, singlePopup = true ) => {
        let popupsToDelete = [];
        if ( singlePopup === true ) {
            popupsToDelete.push( popupId );
        } else {
            popupsToDelete = selectedPopups;
        }

        const formData = {
            action: 'sbr_review_alert_bulk_delete',
            nonce: sbCustomizer.nonce,
            ids: popupsToDelete
        };

        const notificationsContent = {
            success: {
                icon: 'success',
                text: __( 'Review alert deleted', 'reviews-feed' )
            }
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => {
                const remainingPopups = popupsList.filter( p => !popupsToDelete.includes( p.id ) );
                const newCount = data?.data?.popupsCount ?? ( popupsCount - popupsToDelete.length );
                const newTotalPages = Math.ceil( newCount / perPage ) || 1;

                // If current page is now empty and there are still items, reset to page 1
                if ( remainingPopups.length === 0 && newCount > 0 ) {
                    setCurrentPage( 1 );
                    setPopupsCount( newCount );
                    setTotalPages( newTotalPages );
                    fetchPopups( 1 );
                } else if ( currentPage > newTotalPages ) {
                    // If current page exceeds new total pages, go to last available page
                    setCurrentPage( newTotalPages );
                    setPopupsCount( newCount );
                    setTotalPages( newTotalPages );
                    fetchPopups( newTotalPages );
                } else {
                    // Update local state without fetching (use functional update to avoid stale closure)
                    setPopupsList( prevList => prevList.filter( p => !popupsToDelete.includes( p.id ) ) );
                    setPopupsCount( newCount );
                    setTotalPages( newTotalPages );
                }
                setSelectedPopups( [] );
            },
            editorTopLoader,
            editorNotification,
            notificationsContent
        );
    };

    /**
     * Open dialog to delete popup(s)
     */
    const deletePopup = ( popup, singlePopup = true ) => {
        const deleteHeading = ( singlePopup && popup )
            ? __( 'Delete "', 'reviews-feed' ) + popup.name + '"?'
            : __( 'Delete review alerts?', 'reviews-feed' );

        const confirmDialogInfo = {
            active: true,
            heading: deleteHeading,
            description: singlePopup
                ? __( 'Are you sure you want to delete this review alert? This action cannot be undone.', 'reviews-feed' )
                : __( 'Are you sure you want to delete the selected review alerts? This action cannot be undone.', 'reviews-feed' ),
            confirm: {
                text: __( 'Delete', 'reviews-feed' ),
                icon: 'trash',
                onConfirm: () => {
                    deletePopupConfirm( ( singlePopup && popup ) ? popup.id : null, singlePopup );
                    editorConfirmDialog.setConfirmDialog( { active: false } );
                }
            },
            cancel: {
                text: __( 'Cancel', 'reviews-feed' )
            }
        };

        editorConfirmDialog.setConfirmDialog( confirmDialogInfo );
    };

    /**
     * Select/deselect popup
     *
     * Uses functional update form to prevent stale closure bug when
     * rapidly selecting multiple checkboxes (React batches state updates).
     */
    const selectPopup = ( popupId ) => {
        const allPopups = Object.values( popupsList );

        if ( popupId === 'all' ) {
            // Toggle all: clear if all selected, otherwise select all
            setSelectedPopups( prevSelected =>
                prevSelected.length === allPopups.length
                    ? []
                    : allPopups.map( popup => popup.id )
            );
        } else {
            // Toggle individual: add if not selected, remove if selected
            setSelectedPopups( prevSelected =>
                prevSelected.includes( popupId )
                    ? prevSelected.filter( id => id !== popupId )
                    : [ ...prevSelected, popupId ]
            );
        }
    };

    /**
     * Count items in a visibility group
     */
    const countVisibilityItems = ( group ) => {
        if ( !group ) return 0;
        const pages = group.pages?.length || 0;
        const categories = group.categories?.length || 0;
        const postTypes = group.custom_post_types?.length || 0;
        return pages + categories + postTypes;
    };

    /**
     * Check if popup has empty visibility settings
     * Returns true if in "specific" mode with no pages/categories/custom_post_types selected
     */
    const hasEmptyVisibility = ( popup ) => {
        const visibility = popup.settings?.visibility;
        const displayOn = visibility?.display_on || 'specific';

        // Only check if in "specific" mode
        if ( displayOn !== 'specific' ) {
            return false;
        }

        return countVisibilityItems( visibility?.specific ) === 0;
    };

    /**
     * Get visibility location count text for confirmation dialogs
     * Returns a user-friendly string like "12 locations" or "all pages"
     */
    const getVisibilityCountText = ( popup ) => {
        const visibility = popup.settings?.visibility;
        const displayOn = visibility?.display_on;

        // If display_on is 'all' or undefined, show "all pages"
        if ( displayOn === 'all' || displayOn === undefined ) {
            const excludeCount = countVisibilityItems( visibility?.excluded );
            if ( excludeCount > 0 ) {
                return __( 'all pages', 'reviews-feed' ) + ` (${excludeCount} ` + __( 'excluded', 'reviews-feed' ) + ')';
            }
            return __( 'all pages', 'reviews-feed' );
        }

        // Specific pages mode
        const count = countVisibilityItems( visibility?.specific );
        if ( count === 0 ) {
            return __( '0 locations', 'reviews-feed' );
        }
        return count + ' ' + ( count === 1 ? __( 'location', 'reviews-feed' ) : __( 'locations', 'reviews-feed' ) );
    };

    /**
     * Toggle popup status (active/inactive)
     * Uses optimistic update to prevent list reordering
     */
    const togglePopupStatus = ( popupId ) => {
        const popup = popupsList.find( p => p.id === popupId );
        const originalStatus = popup?.status;
        const newStatus = originalStatus === 'active' ? 'inactive' : 'active';

        // Optimistic update - update status locally without reordering
        const updatedList = popupsList.map( p =>
            p.id === popupId ? { ...p, status: newStatus } : p
        );
        setPopupsList( updatedList );

        const formData = {
            action: 'sbr_review_alert_toggle_status',
            nonce: sbCustomizer.nonce,
            id: popupId
        };

        const notificationsContent = {
            success: {
                icon: 'success',
                text: newStatus === 'active'
                    ? __( 'Review alert published!', 'reviews-feed' )
                    : __( 'Review alert reverted to draft.', 'reviews-feed' )
            }
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => {
                // On error, revert only this popup to original status (using functional update
                // to avoid stale closure issues when multiple toggles are in-flight)
                if ( data?.success === false || data?.error ) {
                    setPopupsList( prevList => prevList.map( p =>
                        p.id === popupId ? { ...p, status: originalStatus } : p
                    ) );
                    return;
                }
                // Only update count, don't replace list to avoid reordering
                if ( data?.data?.popupsCount !== undefined ) {
                    setPopupsCount( data.data.popupsCount );
                }
            },
            editorTopLoader,
            editorNotification,
            notificationsContent
        );
    };

    /**
     * Handle toggle status with confirmation dialog
     * Shows appropriate dialog based on current status before toggling
     */
    const handleToggleStatus = ( popup ) => {
        const isCurrentlyPublished = popup.status === 'active';
        const locationText = getVisibilityCountText( popup );

        if ( isCurrentlyPublished ) {
            // Reverting to draft - show confirmation with location count
            const isEmpty = hasEmptyVisibility( popup );

            // Different description for empty vs configured visibility
            const description = isEmpty
                ? __( 'This review alert is published but not visible anywhere (no locations configured). Reverting to draft will unpublish it without affecting your site.', 'reviews-feed' )
                : __( 'This review alert is currently live on', 'reviews-feed' ) + ' ' + locationText + '. ' + __( 'Reverting will stop it from appearing on your site until you publish again.', 'reviews-feed' );

            const confirmDialogInfo = {
                active: true,
                heading: __( 'Revert to draft?', 'reviews-feed' ),
                description: description,
                confirm: {
                    text: __( 'Revert to Draft', 'reviews-feed' ),
                    type: 'primary',
                    icon: 'reset',
                    onConfirm: () => {
                        togglePopupStatus( popup.id );
                    }
                },
                cancel: {
                    text: __( 'Cancel', 'reviews-feed' ),
                    type: 'secondary'
                }
            };
            editorConfirmDialog.setConfirmDialog( confirmDialogInfo );
        } else {
            // Publishing - show confirmation with location count
            const isEmpty = hasEmptyVisibility( popup );
            const description = isEmpty
                ? __( 'No pages configured. This alert won\'t be visible anywhere.', 'reviews-feed' )
                : __( 'This review alert will start appearing on', 'reviews-feed' ) + ' ' + locationText + ' ' + __( 'once published.', 'reviews-feed' );
            const confirmDialogInfo = {
                active: true,
                heading: __( 'Ready to publish?', 'reviews-feed' ),
                description: description,
                confirm: {
                    text: __( 'Publish', 'reviews-feed' ),
                    type: 'primary',
                    icon: 'arrow-top',
                    onConfirm: () => {
                        togglePopupStatus( popup.id );
                    }
                },
                cancel: {
                    text: __( 'Cancel', 'reviews-feed' ),
                    type: 'secondary'
                }
            };
            editorConfirmDialog.setConfirmDialog( confirmDialogInfo );
        }
    };

    /**
     * Get theme display name
     */
    const getThemeDisplayName = ( settings ) => {
        const theme = settings?.theme || 'light';
        // Handle hyphenated themes (e.g., 'minimal-dark' → 'Minimal Dark')
        const themeName = theme
            .split( '-' )
            .map( word => word.charAt( 0 ).toUpperCase() + word.slice( 1 ) )
            .join( ' ' );
        return themeName;
    };

    /**
     * Fetch popups for a specific page via AJAX
     */
    const fetchPopups = ( page ) => {
        setIsLoading( true );

        const formData = {
            action: 'sbr_review_alert_list',
            nonce: sbCustomizer.nonce,
            page: page
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => {
                if ( data?.data?.popupsList ) {
                    setPopupsList( data.data.popupsList );
                }
                if ( data?.data?.popupsCount !== undefined ) {
                    setPopupsCount( data.data.popupsCount );
                }
                if ( data?.data?.totalPages !== undefined ) {
                    setTotalPages( data.data.totalPages );
                }
                setIsLoading( false );
            },
            editorTopLoader,
            null, // No notification on page change
            null
        );
    };

    /**
     * Handle page change
     */
    const handlePageChange = ( page ) => {
        setCurrentPage( page );
        fetchPopups( page );
    };

    return (
        <div className="sb-feedslist-ctn sb-fs">
            <div className="sb-feedslist-top">
                <div className="sb-feedslist-bulk-ctn">
                    <Select
                        size="small"
                        customClass="sb-blulk-select"
                        onChange={ ( event ) => {
                            setBulkAction( event.currentTarget.value );
                        } }
                    >
                        {
                            Object.keys( bulkActionsList ).map( action => {
                                return (
                                    <option key={ action } value={ action }>{ bulkActionsList[ action ] }</option>
                                );
                            } )
                        }
                    </Select>
                    <Button
                        type="secondary"
                        size="small"
                        boxshadow="false"
                        text={ __( 'Apply', 'reviews-feed' ) }
                        onClick={ () => {
                            applyBulkAction();
                        } }
                    />
                </div>
                <div className="sb-feedslist-pagination">
                    <Pagination
                        currentPage={ currentPage }
                        totalPages={ totalPages }
                        totalItems={ popupsCount }
                        onPageChange={ handlePageChange }
                        isLoading={ isLoading }
                    />
                </div>
            </div>

            <div className="sb-feedslist-table-wrap sb-fs">
                <table className="sb-feedslist-table sb-text-tiny">
                    <thead className="sb-feedslist-thtf sb-feedslist-thead sb-dark2-text">
                        <tr>
                            <th>
                                <Checkbox
                                    value={ selectedPopups.length === popupsList.length && popupsList.length > 0 }
                                    enabled={ true }
                                    onChange={ () => {
                                        selectPopup( 'all' );
                                    } }
                                />
                            </th>
                            <th>
                                <span>{ __( 'Name', 'reviews-feed' ) }</span>
                            </th>
                            <th>
                                <span>{ __( 'Status', 'reviews-feed' ) }</span>
                            </th>
                            <th>
                                <span>{ __( 'Displayed on', 'reviews-feed' ) }</span>
                            </th>
                            <th className="cff-fd-lst-act-th">
                                <span>{ __( 'Actions', 'reviews-feed' ) }</span>
                            </th>
                        </tr>
                    </thead>

                    <tbody className="sb-feedslist-tbody">
                        {
                            popupsList.map( ( popup, popupIn ) => {
                                return (
                                    <tr key={ popupIn }>
                                        <td>
                                            <Checkbox
                                                value={ selectedPopups.includes( popup.id ) }
                                                enabled={ true }
                                                onChange={ () => {
                                                    selectPopup( popup.id );
                                                } }
                                            />
                                        </td>
                                        <td className="sb-feedslist-name-ctn">
                                            <a
                                                className="sb-text-small"
                                                href={ `${sbCustomizer.builderUrl}&popup_id=${popup.id}` }
                                            >
                                                { popup.name || __( 'Untitled Alert', 'reviews-feed' ) }
                                            </a>
                                            <span>{ getThemeDisplayName( popup.settings ) }</span>
                                        </td>
                                        <td>
                                            <Switcher
                                                value={ popup.status }
                                                enabled="active"
                                                onChange={ () => handleToggleStatus( popup ) }
                                                label={ popup.status === 'active' ? __( 'Published', 'reviews-feed' ) : __( 'Draft', 'reviews-feed' ) }
                                            />
                                        </td>
                                        <td>
                                            <DisplayedOnTooltip
                                                popup={ popup }
                                                availablePages={ availablePages }
                                                availableCategories={ availableCategories }
                                                availablePostTypes={ availablePostTypes }
                                            />
                                        </td>
                                        <td className="sb-feed-item-actions">
                                            <div className="sb-feed-item-btns">
                                                <div className="sb-relative">
                                                    <Button
                                                        type="secondary"
                                                        size="small"
                                                        iconSize="14"
                                                        boxshadow={ false }
                                                        icon="pen"
                                                        tooltip={ __( 'Edit', 'reviews-feed' ) }
                                                        onClick={ () => {
                                                            window.location.href = `${sbCustomizer.builderUrl}&popup_id=${popup.id}`;
                                                        } }
                                                        customClass="sb-feed-action-btn sb-feed-edit-btn"
                                                    />
                                                </div>
                                                <div className="sb-relative">
                                                    <Button
                                                        type="secondary"
                                                        size="small"
                                                        iconSize="11"
                                                        boxshadow={ false }
                                                        icon="duplicate"
                                                        tooltip={ __( 'Duplicate', 'reviews-feed' ) }
                                                        onClick={ () => {
                                                            duplicatePopup( popup.id );
                                                        } }
                                                        customClass="sb-feed-action-btn sb-feed-duplicate-btn"
                                                    />
                                                </div>
                                                <div className="sb-relative">
                                                    <Button
                                                        type="secondary"
                                                        size="small"
                                                        iconSize="11"
                                                        boxshadow={ false }
                                                        icon="trash"
                                                        tooltip={ __( 'Delete', 'reviews-feed' ) }
                                                        onClick={ () => {
                                                            deletePopup( popup );
                                                        } }
                                                        customClass="sb-feed-action-btn sb-feed-delete-btn"
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            } )
                        }
                    </tbody>

                    <tfoot className="sb-feedslist-thtf sb-feedslist-tfoot sb-dark2-text">
                        <tr>
                            <th>
                                <Checkbox
                                    value={ selectedPopups.length === popupsList.length && popupsList.length > 0 }
                                    enabled={ true }
                                    onChange={ () => {
                                        selectPopup( 'all' );
                                    } }
                                />
                            </th>
                            <th>
                                <span className="sb-text-tiny sb-dark2-text">{ __( 'Name', 'reviews-feed' ) }</span>
                            </th>
                            <th>
                                <span className="sb-text-tiny sb-dark2-text">{ __( 'Status', 'reviews-feed' ) }</span>
                            </th>
                            <th>
                                <span className="sb-text-tiny sb-dark2-text">{ __( 'Displayed on', 'reviews-feed' ) }</span>
                            </th>
                            <th className="cff-fd-lst-act-th">
                                <span className="sb-text-tiny sb-dark2-text">{ __( 'Actions', 'reviews-feed' ) }</span>
                            </th>
                        </tr>
                    </tfoot>
                </table>

                {/* Bottom Pagination */}
                <div className="sb-feedslist-bottom">
                    <div className="sb-feedslist-pagination sb-feedslist-pagination-bottom">
                        <Pagination
                            currentPage={ currentPage }
                            totalPages={ totalPages }
                            totalItems={ popupsCount }
                            onPageChange={ handlePageChange }
                            isLoading={ isLoading }
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewAlertsList;
